import type { GhmSignalKind } from "@ghm/mqtt-contract";

import { formatPublicSignalLabel, formatTag } from "@/features/machines/addressing";
import type { ChannelAddress, SignalType } from "@/features/machines/types";
import type { LatestPort } from "@/features/devices/types";
import type { WidgetType } from "@/features/widgets/types";
import type { SignalConfig, SignalRole, SignalScale } from "./types";

interface BuildDefaultSignalConfigOptions {
  machineId: string;
  slotIndex: number;
  port: LatestPort;
  signal: SignalType;
  /** Semantica declarada no `/meta`. Ausente = comportamento anterior. */
  kind?: GhmSignalKind;
  /** Faixa de engenharia declarada (`values.v.min`/`max` do analogico). */
  declaredRange?: { min?: number; max?: number };
  /** Casas decimais declaradas. */
  declaredDecimals?: number;
}

export function buildDefaultSignalConfig({
  machineId,
  slotIndex,
  port,
  signal,
  kind,
  declaredRange,
  declaredDecimals,
}: BuildDefaultSignalConfigOptions): SignalConfig {
  const address: ChannelAddress = {
    kind: "real",
    slotIndex,
    portIndex: port.portIndex,
    signal,
  };
  const mode = String(port.mode ?? "").toUpperCase();

  return {
    source: formatTag(address),
    machineId,
    label: inferSignalLabel(address, mode, kind),
    signal,
    kind,
    role: inferSignalRole(signal, mode, kind),
    unit: inferSignalUnit(signal, mode),
    activeWhen: signal === "s" ? 1 : undefined,
    stateLabels:
      signal === "s"
        ? {
            on: mode === "HSC" ? "Horario" : "Ligado",
            off: mode === "HSC" ? "Parado" : "Desligado",
            unavailable: "Sem sinal",
          }
        : undefined,
    scale: inferSignalScale(signal, mode, port, declaredRange, declaredDecimals),
    preferredWidget: inferPreferredWidget(signal, kind),
  };
}

function inferSignalLabel(
  address: ChannelAddress,
  mode: string,
  kind?: GhmSignalKind,
): string {
  const baseLabel = formatPublicSignalLabel(address, kind);
  if (address.signal !== "v" || mode !== "PM") return baseLabel;
  return baseLabel.replace("Velocidade", "Vazao");
}

export function mergeSignalConfig(
  defaults: SignalConfig,
  saved: SignalConfig | undefined,
): SignalConfig {
  if (!saved) return defaults;

  return {
    ...defaults,
    ...saved,
    source: defaults.source,
    machineId: defaults.machineId,
    signal: defaults.signal,
    label: saved.label.trim() || defaults.label,
    // `kind` vem do hardware, nao do operador: o declarado no `/meta` vence
    // sempre. Um config salvo antes do campo existir nao pode "fixar" a
    // ausencia de semantica.
    kind: defaults.kind ?? saved.kind,
    scale: saved.scale ?? defaults.scale,
    preferredWidget: saved.preferredWidget ?? defaults.preferredWidget,
    // Preferências do operador têm prioridade. Quando ausentes, mantemos
    // os defaults (que provavelmente também são `undefined`, gerando
    // fallback automático no `WidgetPanel`).
    hidden: saved.hidden ?? defaults.hidden,
    size: saved.size ?? defaults.size,
    thresholds: saved.thresholds ?? defaults.thresholds,
  };
}

/**
 * Papel do sinal. O `kind` corrige o palpite antigo, que chamava TODO `v` de
 * "speed" — e um `v` de tensao ou temperatura nao e velocidade de nada.
 * Sem `kind`, mantem o comportamento historico.
 */
/**
 * Papel do sinal. UNICA tabela de papeis do app: os defaults do sinal e a
 * config derivada de um widget salvo passam os dois por aqui, senao um
 * salvamento rebaixava em silencio o papel que os defaults tinham acertado
 * (um `s` de HSC vira "direction", um `v` de PM sem `kind` vira "flow" — e
 * nenhum dos dois e dedutivel sem o modo).
 */
export function inferSignalRole(
  signal: SignalType,
  mode: string,
  kind?: GhmSignalKind,
): SignalRole {
  if (signal === "s") return mode === "HSC" ? "direction" : "state";
  if (signal === "acc") return "total";
  if (signal === "v") {
    if (kind === "cumulative") return "total";
    if (kind === "state") return "state";
    // Grandeza fisica instantanea nao e ritmo: `technical` e o papel honesto
    // ate existir um vocabulario melhor de papeis.
    if (kind === "instantaneous") return "technical";
    return mode === "PM" ? "flow" : "speed";
  }
  return "technical";
}

function inferSignalUnit(signal: SignalType, mode: string): string | undefined {
  if (signal === "v") {
    if (mode === "PM") return "L/min";
    if (mode === "CT") return "un/min";
    if (mode === "HSC") return "m/min";
  }

  if (signal === "acc") {
    if (mode === "PM") return "L";
    if (mode === "CT") return "un";
    if (mode === "HSC") return "m";
  }

  return undefined;
}

/**
 * Escala do sinal. Quando o `/meta` DECLARA a faixa de engenharia (analogico),
 * ela vence: o configurador ja sabe que o sensor vai de 0 a 10 bar, e derivar
 * `valor * 1.35` da primeira leitura produzia um fundo de escala que muda
 * conforme a hora em que o operador abriu a tela.
 */
function inferSignalScale(
  signal: SignalType,
  mode: string,
  port: LatestPort,
  declaredRange?: { min?: number; max?: number },
  declaredDecimals?: number,
): SignalScale | undefined {
  if (signal === "s") return { min: 0, max: 1, decimals: 0 };

  const decimals =
    declaredDecimals !== undefined && Number.isInteger(declaredDecimals)
      ? declaredDecimals
      : mode === "DI" || mode === "LATCH" || mode === "RST" || (signal === "acc" && mode === "CT")
        ? 0
        : 1;

  const declaredMin = toFiniteNumber(declaredRange?.min);
  const declaredMax = toFiniteNumber(declaredRange?.max);
  if (signal === "v" && declaredMin !== null && declaredMax !== null && declaredMax > declaredMin) {
    return { min: declaredMin, max: declaredMax, decimals };
  }

  const rawValue = signal === "v" ? port.v : port.acc;
  const numericValue = toFiniteNumber(rawValue);
  const fallbackMax = signal === "v" ? 100 : 1000;
  const max = numericValue === null ? fallbackMax : Math.max(10, Math.ceil(numericValue * 1.35));

  return { min: 0, max, decimals };
}

/**
 * Widget padrão por tipo de sinal. A escolha é guiada antes de tudo pelo
 * `signal`:
 *  - `s` (estado) → LED, com polaridade configurável.
 *  - `v` (taxa instantânea) → gauge, que combina com velocidade/vazão.
 *  - `acc` (totalizador) → número grande; mesmo em PM/HSC o acumulado
 *    é um contador, então gauge confundiria o operador.
 *
 * Antes desta refatoração, `acc` em PM/HSC caía em gauge, o que gerava
 * um ponteiro andando para um valor que só cresce.
 */
function inferPreferredWidget(signal: SignalType, kind?: GhmSignalKind): WidgetType {
  if (signal === "s") return "led";
  if (signal === "v") {
    // Velocimetro sobre algo que so cresce (ou sobre um codigo) e ponteiro que
    // nunca volta — mesma regra da matriz de compatibilidade.
    return kind === "cumulative" || kind === "state" ? "number" : "gauge";
  }
  return "number";
}

function toFiniteNumber(value: unknown): number | null {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}
